<template>
  <div class="dashboard-panel">
    <!-- Desktop: 테이블 -->
    <div v-if="isPcRedesign" class="sq-table-wrapper">
      <div class="sq-table-scroll">
        <table class="sq-table">
          <thead>
            <tr>
              <th rowspan="2" class="sq-th sq-th--sticky">{{ $t('dashboard.category') }}</th>
              <th rowspan="2" class="sq-th sq-th--sticky">{{ $t('dashboard.item') }}</th>
              <th colspan="5" class="sq-th">{{ $t('dashboard.processingDays') }}</th>
              <th rowspan="2" class="sq-th">{{ $t('dashboard.unprocessedRate') }}<br />({{ $t('dashboard.pending') }})</th>
              <th rowspan="2" class="sq-th">{{ $t('dashboard.reRepairRate') }}<br />({{ $t('dashboard.withinTwoMonths') }})</th>
            </tr>
            <tr>
              <th class="sq-th">{{ $t('dashboard.summation') }}</th>
              <th class="sq-th">{{ $t('dashboard.withinOneDay') }}</th>
              <th class="sq-th">{{ $t('dashboard.withinThreeDays') }}</th>
              <th class="sq-th">{{ $t('dashboard.threeDaysPassed') }}</th>
              <th class="sq-th">{{ $t('dashboard.sevenDaysPassed') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(q, idx) in serviceQuality.partnerServiceQualitys" :key="'pt-' + idx">
              <td v-if="idx === 0" :rowspan="serviceQuality.partnerServiceQualitys.length" class="sq-td sq-td--category">
                {{ $t('dashboard.partner') }}
              </td>
              <td class="sq-td sq-td--name">{{ q.partnerName }}</td>
              <td class="sq-td sq-td--number">{{ q.summation }}</td>
              <td class="sq-td sq-td--number">{{ q.withinOneDay }}</td>
              <td class="sq-td sq-td--number">{{ q.withinThreeDays }}</td>
              <td class="sq-td sq-td--number" :class="{ 'sq-td--warn': q.threeDaysPassed > 0 }">{{ q.threeDaysPassed }}</td>
              <td class="sq-td sq-td--number" :class="{ 'sq-td--danger': q.sevenDaysPassed > 0 }">{{ q.sevenDaysPassed }}</td>
              <td class="sq-td sq-td--rate">{{ formatRate(q.unprocessedRate) }}%</td>
              <td class="sq-td sq-td--rate">{{ formatRate(q.reoccurrenceRate) }}%</td>
            </tr>
          </tbody>
          <tbody v-if="serviceQuality.productModelServiceQualitys.length" class="sq-table__section-divider">
            <tr v-for="(q, idx) in serviceQuality.productModelServiceQualitys" :key="'pm-' + idx">
              <td v-if="idx === 0" :rowspan="serviceQuality.productModelServiceQualitys.length" class="sq-td sq-td--category">
                {{ $t('dashboard.productModel') }}
              </td>
              <td class="sq-td sq-td--name">{{ q.productModelName }}</td>
              <td class="sq-td sq-td--number">{{ q.summation }}</td>
              <td class="sq-td sq-td--number">{{ q.withinOneDay }}</td>
              <td class="sq-td sq-td--number">{{ q.withinThreeDays }}</td>
              <td class="sq-td sq-td--number" :class="{ 'sq-td--warn': q.threeDaysPassed > 0 }">{{ q.threeDaysPassed }}</td>
              <td class="sq-td sq-td--number" :class="{ 'sq-td--danger': q.sevenDaysPassed > 0 }">{{ q.sevenDaysPassed }}</td>
              <td class="sq-td sq-td--rate">{{ formatRate(q.unprocessedRate) }}%</td>
              <td class="sq-td sq-td--rate">{{ formatRate(q.reoccurrenceRate) }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile: 카드 레이아웃 -->
    <div v-else class="m-sq-section">
      <template v-if="serviceQuality.partnerServiceQualitys.length">
        <div class="m-sq-category__title">{{ $t('dashboard.partner') }}</div>
        <div v-for="q in serviceQuality.partnerServiceQualitys" :key="q.partnerId" class="m-sq-card">
          <div class="m-sq-card__header">
            <span class="m-sq-card__name">{{ q.partnerName }}</span>
            <div class="m-sq-card__total-wrap">
              <span class="m-sq-card__total-label">{{ $t('dashboard.summation') }}</span>
              <span class="m-sq-card__total">{{ q.summation }}</span>
            </div>
          </div>
          <div class="m-sq-card__grid">
            <div class="m-sq-metric">
              <span class="m-sq-metric__label">{{ $t('dashboard.withinOneDay') }}</span>
              <span class="m-sq-metric__value">{{ q.withinOneDay }}</span>
            </div>
            <div class="m-sq-metric">
              <span class="m-sq-metric__label">{{ $t('dashboard.withinThreeDays') }}</span>
              <span class="m-sq-metric__value">{{ q.withinThreeDays }}</span>
            </div>
            <div class="m-sq-metric">
              <span class="m-sq-metric__label">{{ $t('dashboard.threeDaysPassed') }}</span>
              <span class="m-sq-metric__value" :class="{ 'm-sq-metric__value--warn': q.threeDaysPassed > 0 }">{{ q.threeDaysPassed }}</span>
            </div>
            <div class="m-sq-metric">
              <span class="m-sq-metric__label">{{ $t('dashboard.sevenDaysPassed') }}</span>
              <span class="m-sq-metric__value" :class="{ 'm-sq-metric__value--danger': q.sevenDaysPassed > 0 }">{{ q.sevenDaysPassed }}</span>
            </div>
          </div>
          <div class="m-sq-card__rates">
            <div class="m-sq-rate">
              <span class="m-sq-rate__label">{{ $t('dashboard.unprocessedRate') }}</span>
              <span class="m-sq-rate__value">{{ formatRate(q.unprocessedRate) }}%</span>
            </div>
            <div class="m-sq-rate">
              <span class="m-sq-rate__label">{{ $t('dashboard.reRepairRate') }}</span>
              <span class="m-sq-rate__value">{{ formatRate(q.reoccurrenceRate) }}%</span>
            </div>
          </div>
        </div>
      </template>

      <template v-if="serviceQuality.productModelServiceQualitys.length">
        <div class="m-sq-category__title" :style="serviceQuality.partnerServiceQualitys.length ? 'margin-top: 16px' : ''">{{ $t('dashboard.productModel') }}</div>
        <div v-for="q in serviceQuality.productModelServiceQualitys" :key="q.productModelId" class="m-sq-card">
          <div class="m-sq-card__header">
            <span class="m-sq-card__name">{{ q.productModelName }}</span>
            <div class="m-sq-card__total-wrap">
              <span class="m-sq-card__total-label">{{ $t('dashboard.summation') }}</span>
              <span class="m-sq-card__total">{{ q.summation }}</span>
            </div>
          </div>
          <div class="m-sq-card__grid">
            <div class="m-sq-metric">
              <span class="m-sq-metric__label">{{ $t('dashboard.withinOneDay') }}</span>
              <span class="m-sq-metric__value">{{ q.withinOneDay }}</span>
            </div>
            <div class="m-sq-metric">
              <span class="m-sq-metric__label">{{ $t('dashboard.withinThreeDays') }}</span>
              <span class="m-sq-metric__value">{{ q.withinThreeDays }}</span>
            </div>
            <div class="m-sq-metric">
              <span class="m-sq-metric__label">{{ $t('dashboard.threeDaysPassed') }}</span>
              <span class="m-sq-metric__value" :class="{ 'm-sq-metric__value--warn': q.threeDaysPassed > 0 }">{{ q.threeDaysPassed }}</span>
            </div>
            <div class="m-sq-metric">
              <span class="m-sq-metric__label">{{ $t('dashboard.sevenDaysPassed') }}</span>
              <span class="m-sq-metric__value" :class="{ 'm-sq-metric__value--danger': q.sevenDaysPassed > 0 }">{{ q.sevenDaysPassed }}</span>
            </div>
          </div>
          <div class="m-sq-card__rates">
            <div class="m-sq-rate">
              <span class="m-sq-rate__label">{{ $t('dashboard.unprocessedRate') }}</span>
              <span class="m-sq-rate__value">{{ formatRate(q.unprocessedRate) }}%</span>
            </div>
            <div class="m-sq-rate">
              <span class="m-sq-rate__label">{{ $t('dashboard.reRepairRate') }}</span>
              <span class="m-sq-rate__value">{{ formatRate(q.reoccurrenceRate) }}%</span>
            </div>
          </div>
        </div>
      </template>

      <div v-if="!serviceQuality.productModelServiceQualitys.length && !serviceQuality.partnerServiceQualitys.length" class="m-sq-empty">
        {{ $t('dashboard.noData') }}
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps */

defineProps({
  serviceQuality: { type: Object, required: true },
  isPcRedesign: { type: Boolean, default: false }
})

function formatRate(val) {
  if (val == null) return '0.00'
  return Number(val).toFixed(2)
}
</script>

<style scoped>
.dashboard-panel {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: var(--radius-xl, 16px);
  border: 1px solid var(--color-gray-100, #E8EAED);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.08));
  padding: 24px;
  transition: box-shadow var(--transition-normal, 0.2s ease), transform var(--transition-normal, 0.2s ease);
}
.dashboard-panel:hover {
  box-shadow: var(--shadow-md, 0 2px 6px rgba(0, 0, 0, 0.1));
  transform: translateY(-1px);
}
.dashboard-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.dashboard-panel__title::before {
  content: '';
  width: 4px;
  height: 16px;
  background: var(--color-primary, #3B5998);
  border-radius: 2px;
  flex-shrink: 0;
}
.sq-table-wrapper {
  position: relative;
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  border: 1px solid var(--color-gray-100, #E8EAED);
}
.sq-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.sq-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.sq-th {
  padding: 10px 12px;
  background: var(--color-gray-50, #F2F3F5);
  color: var(--color-gray-700, #2E333B);
  font-weight: 600;
  font-size: 12px;
  text-align: center;
  border-bottom: 1px solid var(--color-gray-200, #B8BCC4);
  white-space: nowrap;
}
.sq-td {
  padding: 10px 12px;
  text-align: center;
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
  color: var(--color-gray-700, #2E333B);
}
.sq-td--category {
  font-weight: 700;
  background: var(--color-gray-50, #F2F3F5);
  color: var(--color-gray-600, #424750);
}
.sq-td--name { font-weight: 600; text-align: left; }
.sq-td--number { font-weight: 600; font-variant-numeric: tabular-nums; }
.sq-td--rate { font-weight: 700; color: var(--color-primary, #3B5998); }
.sq-td--warn { color: var(--color-warning, #C8870A); font-weight: 700; }
.sq-td--danger { color: var(--color-danger, #C03A3A); font-weight: 700; }
.sq-table__section-divider tr:first-child td {
  border-top: 2px solid var(--color-gray-200, #B8BCC4);
}
.m-sq-section { display: flex; flex-direction: column; gap: 8px; }
.m-sq-category__title {
  font-size: 13px; font-weight: 700; color: var(--color-gray-700, #2E333B);
  padding-left: 10px; border-left: 3px solid var(--color-primary, #3B5998); margin-bottom: 4px;
}
.m-sq-card {
  background: var(--color-white, #fff); border-radius: var(--m-card-radius, 12px);
  border: var(--m-card-border, 1px solid #e5e7eb); box-shadow: var(--m-card-shadow, 0 1px 4px rgba(0, 0, 0, 0.06)); padding: 14px;
}
.m-sq-card__header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid var(--color-gray-100, #E8EAED);
}
.m-sq-card__name { font-size: 14px; font-weight: 700; color: var(--color-gray-800, #1E2228); }
.m-sq-card__total-wrap { display: flex; align-items: baseline; gap: 4px; }
.m-sq-card__total-label { font-size: 11px; color: var(--color-gray-500, #5C616B); font-weight: 500; }
.m-sq-card__total { font-size: 20px; font-weight: 800; color: var(--color-primary, #3B5998); }
.m-sq-card__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 10px; }
.m-sq-metric {
  background: var(--color-gray-50, #F2F3F5); border-radius: var(--radius-md, 8px);
  padding: 8px 10px; display: flex; flex-direction: column; align-items: center; gap: 2px;
}
.m-sq-metric__label { font-size: 11px; color: var(--color-gray-500, #5C616B); font-weight: 500; }
.m-sq-metric__value { font-size: 16px; font-weight: 700; color: var(--color-gray-800, #1E2228); }
.m-sq-metric__value--warn { color: var(--color-warning, #C8870A); }
.m-sq-metric__value--danger { color: var(--color-danger, #C03A3A); }
.m-sq-card__rates { display: flex; gap: 6px; }
.m-sq-rate {
  flex: 1; display: flex; align-items: center; justify-content: space-between;
  background: var(--color-gray-100, #E8EAED); border-radius: var(--radius-md, 8px); padding: 8px 10px;
}
.m-sq-rate__label { font-size: 11px; color: var(--color-gray-500, #5C616B); font-weight: 500; }
.m-sq-rate__value { font-size: 13px; font-weight: 700; color: var(--color-gray-800, #1E2228); }
.m-sq-empty { text-align: center; padding: 40px 0; color: var(--color-gray-500, #5C616B); font-size: 13px; }

@media (max-width: 767px) {
  .dashboard-panel { padding: 16px; border-radius: var(--radius-lg, 12px); }
}
</style>
